import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import FailModal from '../../components/common/FailModal';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';
import { usePagination } from '../../hooks/usePagination';

const CompanyType = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canCompanyType = (key) => can(key, 'companyType');

  // Pagination
  const pagination = usePagination(10);

  // Search state
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

    const [types, setTypes] = useState([]);
    const [newTitle, setNewTitle] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const [editId, setEditId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    const fetchTypes = async () => {
        try {
            const response = await api.get(`/md/company-types-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: appliedSearchTerm
                }
            });
            setTypes(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setTotal(response.data.totalElements || 0);
            pagination.setCurrentPageItems(response.data.content.length);
        } catch (error) {
            let errorMessage = 'Failed to fetch company types.';
            let technicalDetails = '';
            
            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view company types.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while fetching types.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching company types.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }
            
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    useEffect(() => {
        fetchTypes();
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    // Handle adding a new types
    const handleAddTypes = async () => {
        if (!canCompanyType('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        if (newTitle.trim() === '') {
            setFailMessage('Company type name cannot be empty.\n\nTechnical Details: Validation failed - empty name field');
            setShowFailModal(true);
            return;
        }

        try {
            const newTypes = {
                name: newTitle,
            };
            const response = await api.post('/md/company-types', newTypes);
            setTypes([...types, response.data]);
            setNewTitle('');
        } catch (error) {
            let errorMessage = 'Failed to add company type.';
            let technicalDetails = '';
            
            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = 'Invalid company type data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to add company types.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 409:
                        errorMessage = 'A company type with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${newTitle}"`;
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while adding type.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while adding the company type.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }
            
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Handle editing a Types
    const handleEditClick = (types) => {
        if (!canCompanyType('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setEditId(types.id);
        setEditTitle(types.name);
        if (window.bootstrap && window.bootstrap.Modal) {
            const modal = new window.bootstrap.Modal(document.getElementById('modal-edit-types'));
            modal.show();
        }
    };

    const handleUpdateTypes = async () => {
        if (editTitle.trim() === '') {
            setFailMessage('Company type name cannot be empty.\n\nTechnical Details: Validation failed - empty name field');
            setShowFailModal(true);
            return;
        }

        try {
            const updatedTypes = {
                id: editId,
                name: editTitle,
            };
            await api.put(`/md/company-types/${editId}`, updatedTypes);
            setTypes(types.map((item) => (item.id === editId ? { ...item, name: editTitle } : item)));
            setEditId(null);
            setEditTitle('');
        } catch (error) {
            let errorMessage = 'Failed to update company type.';
            let technicalDetails = '';
            
            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = 'Invalid company type data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to update company types.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 404:
                        errorMessage = 'Company type not found.';
                        technicalDetails = 'The requested company type may have been deleted';
                        break;
                    case 409:
                        errorMessage = 'A company type with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${editTitle}"`;
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while updating type.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while updating the company type.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }
            
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Handle deleting a Types
    const handleDeleteTypes = async (id) => {
        try {
            await api.delete(`/md/company-types/${id}`);
            setTypes(types.filter((types) => types.id !== id));

        } catch (error) {
            console.error('Error deleting Types', error);
        }
    };

    // Modal events for clearing input
    useEffect(() => {
        // Modal events for clearing input
        const modal = document.getElementById('modal-types');
        modal?.addEventListener('hidden.bs.modal', () => {
            setNewTitle('');
        });

        const modalEdit = document.getElementById('modal-edit-types');
        modalEdit?.addEventListener('hidden.bs.modal', () => {
            setEditTitle('');
            setEditId(null);
        });

        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
            modalEdit?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);

    const toggleSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === types.length) {
            setSelectedIds([]);
        } else {
            const allIds = types.map((types) => types.id);
            setSelectedIds(allIds);
        }
    };

    const handleCheckboxChange = (id) => {
        setSelectedIds((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter((selectedId) => selectedId !== id)
                : [...prevSelected, id]
        );
    };

    const handleBulkDelete = async () => {
        if (!canCompanyType('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one company type to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }

        try {
            await api.delete('/md/company-types/bulk', { data: selectedIds });
            await fetchTypes();
            setSelectedIds([]);
        } catch (error) {
            let errorMessage = 'Failed to delete company types.';
            let technicalDetails = '';
            
            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to delete these items.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 404:
                        errorMessage = 'One or more company types not found.';
                        technicalDetails = 'Some items may have been already deleted';
                        break;
                    case 500:
                        errorMessage = 'Server error while deleting items.';
                        technicalDetails = error.response.data.message || 'Internal server error during deletion';
                        break;
                    default:
                        errorMessage = 'An error occurred while deleting the items.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred during deletion.';
                technicalDetails = error.message;
            }
            
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) {
            alert('Pilih data yang ingin dihapus.');
        }
        return;

        if (!window.confirm('Yakin ingin menghapus data yang dipilih?')) return;

        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`/md/company-types/${id}`))
            );

            // Update state setelah delete
            setTypes(types.filter((types) => !selectedIds.includes(types.id)));
            setSelectedIds([]);
        } catch (error) {
            console.error('Error deleting selected types:', error);
        }
    };

    return (
        <>

                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 class="page-title d-flex align-items-center">
                                    <a class="me-2 d-flex align-items-center" href="./masterdata-main.html">
                                        <span class="d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M15 6l-6 6l6 6" />
                                            </svg>
                                        </span>
                                    </a>
                                    Company Types
                                </h2>

                            </div>
                            <div className="col-auto ms-auto d-print-none">

                                <div class="btn-list">
								<a href="#" class="btn btn-primary btn-5 d-none d-sm-inline-block"
									data-bs-toggle="modal" data-bs-target="#modal-types">
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
										fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
										stroke-linejoin="round" class="icon icon-2">
										<path d="M12 5l0 14" />
										<path d="M5 12l14 0" />
									</svg>
									Create
								</a>
								<a href="#" class="btn btn-primary btn-6 d-sm-none btn-icon" data-bs-toggle="modal"
									data-bs-target="#modal-types" aria-label="Create new report">
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
										fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
										stroke-linejoin="round" class="icon icon-2">
										<path d="M12 5l0 14" />
										<path d="M5 12l14 0" />
									</svg>
								</a>
							</div>

                            </div>
                        </div>
                    </div>
                </div>

                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                              <div className="card-body">
                                <GenericView
                                  data={types}
                                  columns={[
                                    { key: 'name', label: 'Name', sortable: true },
                                    { key: 'cuser', label: 'Created By', sortable: true, render: (value, row) => row.cuser?.username || '-' },
                                    { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
                                  ]}
                                  isLoading={capabilitiesLoading}
                                  emptyStateMessage="No company types found"
                                  checkboxSelection={true}
                                  selectedRows={selectedIds}
                                  onSelectRow={(itemId, isSelected) => {
                                    if (isSelected) {
                                      setSelectedIds([...selectedIds, itemId]);
                                    } else {
                                      setSelectedIds(selectedIds.filter(id => id !== itemId));
                                    }
                                  }}
                                  onSelectAllRows={(itemIds) => {
                                    if (Array.isArray(itemIds) && itemIds.length > 0) {
                                      setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                    } else {
                                      setSelectedIds([]);
                                    }
                                  }}
                                  onEdit={canCompanyType('edit') ? (item) => {
                                    setSelectedIds([item.id]);
                                    handleEditClick(item);
                                  } : undefined}
                                  onDelete={canCompanyType('delete') ? (item) => {
                                    setSelectedIds([item.id]);
                                    handleBulkDelete();
                                  } : undefined}
                                  searchComponent={
                                    <div className="d-flex gap-2">
                                      <SearchInput
                                        value={searchTerm}
                                        onChange={handleSearchInputChange}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchSubmit();
                                          }
                                        }}
                                        placeholder="Search..."
                                        uniqueId="company-type-search"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSearchSubmit}
                                      >
                                        Search
                                      </button>
                                    </div>
                                  }
                                  actionButtons={
                                    <div className="d-flex gap-2">
                                      {canCompanyType('delete') && (
                                        <div ref={actionsContainerRef} className="dropdown">
                                          <a
                                            ref={actionsToggleRef}
                                            href="#"
                                            className="btn dropdown-toggle"
                                            data-bs-toggle="dropdown"
                                            aria-expanded={isActionsOpen}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              const inst = actionsDropdownInstanceRef.current;
                                              if (inst?.toggle) inst.toggle();
                                              else setIsActionsOpen(prev => !prev);
                                            }}
                                          >Actions</a>
                                          <div className="dropdown-menu">
                                            {canCompanyType('delete') && (
                                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleBulkDelete(); }}
                                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                Delete
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  }
                                  pagination={{
                                    currentPage: pagination.page + 1,
                                    totalRows: pagination.total,
                                    rowsPerPage: pagination.size,
                                    onPageChange: (page) => pagination.setPage(page - 1),
                                    onRowsPerPageChange: (size) => {
                                      pagination.setSize(size);
                                      pagination.setPage(0);
                                    }
                                  }}
                                  pageSizeOptions={[10, 25, 50, 100]}
                                  uniqueId="company-type-table"
                                />
                              </div>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Modal Add Types */}
            <div className="modal modal-blur fade" id="modal-types" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Company Types</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    placeholder="Enter Company Types"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">Batal</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={handleAddTypes}>Save</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Edit Types */}
            <div className="modal modal-blur fade" id="modal-edit-types" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Types</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder="Edit Types name"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">Batal</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={handleUpdateTypes}>Save</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add FailModal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Operation Failed"
                message={failMessage}
                buttonText="Close"
            />
        </>
    );
};

export default CompanyType;
